import { VideoParams } from './video.viewModel';
import { Modal } from '@design-system/components/modal/modal';
import React, { useState, useEffect, useCallback } from 'react';
import { Typography } from '@design-system/components/typography/typography';
import { Col, Row } from '@design-system/components/grid/grid';
import { Button } from '@design-system/components/button/button';
import { useFileUploadViewModel } from '@client/new/shared/file-upload';
import { TemplatesList } from '@client/new/pages/challenges/view/components/TemplatesList';
import { PreviewFrame } from '@client/new/components/Mux/PreviewFrame';
import { StoryboardStrip } from '@client/new/components/Mux/MuxStoryboard';

type VideoThumbnailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  duration: number; // seconds
  onThumbnailPicked: (
    p: Pick<VideoParams, 'thumbnailCustomURL' | 'thumbnailTimestampMs'>,
  ) => void;
  muxPlaybackId: string;
  thumbnail: {
    url?: string | null;
    time: number;
  };
};

export const VideoThumbnailModal = (p: VideoThumbnailModalProps) => {
  const [thumbUrl, setThumbUrl] = useState(p.thumbnail.url || '');
  const vm = useFileUploadViewModel({
    onChange: setThumbUrl,
  });

  const [t, setT] = useState(p.thumbnail.time / 1000);

  const handleTimeChange = useCallback((newTime: number) => {
    setT(newTime);
  }, []);

  useEffect(() => {
    setT(p.thumbnail.time / 1000);
  }, [p.thumbnail.time]);

  useEffect(() => {
    setThumbUrl(p.thumbnail.url || '');
  }, [p.thumbnail.url]);

  return (
    <Modal
      isOpen={p.isOpen}
      onClose={p.onClose}
      disableBackdropClick={true}
      footerClassName="p-0"
      title="Edit"
      titleClassName="text-center"
      modalClassName="w-full md:w-[800px]"
      modalContentClassName="overflow-hidden"
      primaryCta={{
        caption: 'Save',
        onClick: () => {
          p.onThumbnailPicked({
            thumbnailTimestampMs: Math.round(t * 1000),
            thumbnailCustomURL: thumbUrl,
          });
        },
      }}
      secondaryCta={{
        caption: 'Cancel',
        onClick: p.onClose,
      }}
    >
      <PreviewFrame
        playbackId={p.muxPlaybackId}
        time={t}
        customThumbnailUrl={thumbUrl}
      />

      <Row alignCenter>
        <Typography fontWeight="medium">Thumbnail</Typography>
        <Col>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <input
              type="file"
              accept="image/jpeg,image/png"
              ref={vm.uploadInputRef}
              onChange={(ev) =>
                ev.target.files &&
                vm.dispatch('UploadBanner', ev.target.files[0])
              }
              multiple={false}
              hidden
            />
            <Button
              variant="text"
              size="medium"
              color="primary"
              onClick={() => vm.dispatch('StartBannerUpload')}
              loading={vm.isUploading}
            >
              Select from computer
            </Button>
          </div>
        </Col>
      </Row>

      <Row>
        {!thumbUrl && (
          <Typography variant="body" fontWeight="normal">
            Use the slider to select the frame for the thumbnail. The thumbnail
            image will be scaled to fill the 4:3 image ratio.
          </Typography>
        )}
      </Row>
      <Row>
        {vm.error && (
          <Typography variant="body" size="small" color="ERROR-100">
            {vm.error}
          </Typography>
        )}
      </Row>
      {thumbUrl ? (
        <TemplatesList
          urls={[thumbUrl || '']}
          onRemove={() => vm.dispatch('Remove')}
        />
      ) : (
        <StoryboardStrip
          playbackId={p.muxPlaybackId}
          duration={p.duration}
          value={t}
          onChange={handleTimeChange}
          onChangeEnd={handleTimeChange}
        />
      )}
    </Modal>
  );
};
